import { ELanguage } from '@ringana/configs';
import { IBooklet } from '@/common/interfaces/magazine.interface';
import { IInterestingArticle } from '@/common/interfaces/interesting-for-you.interface';
import { IArticleCard } from '@/common/interfaces/article-card.interface';
import { IShippingCost } from '@/common/interfaces/shipping-info.interface';
import WpApiHelper from '@/helpers/WpApiHelper';
import { IPostId } from '@/common/interfaces/index.interface';
import { IMediaPost } from '@/common/interfaces/media.interface';
import { IPreviewBlogPost, ISliderBlogPost } from '@/common/interfaces/ringana-blog.interfaces';
import { IKnowledgeBaseOverview } from '@/common/interfaces/knowledge-base-overview.interface';
import { INewsEntry } from '../common/interfaces/news.interface';
import { IMenus } from '../common/interfaces/header.interface';
import { TApiWpPages } from '../common/types/api.types';
import WpApiRoutes from '../api/WpApiRoutes';
import { IExtendedSingleProductData, ISingleProductData } from '../common/interfaces/products.interface';
import { ISummitDataWP } from '@/common/interfaces/summits.interface';
import { ISingleSummitWPData } from '@/common/interfaces/single-incentive.interface';
import { ITransparencyReport } from '../common/interfaces/transparency-reports.interface';

export default class WpApiService {
  public async getInterestingForYou(id: number, lang: ELanguage): Promise<IInterestingArticle[]> {
    const response = await WpApiRoutes.getInterestingForYou.fetch({
      data: {
        id,
        lang,
      },
    });
    return response.data;
  }

  public async getBooklets(action: string, lang: ELanguage): Promise<IBooklet[]> {
    const response = await WpApiRoutes.getBooklets.fetch({
      data: {
        action,
        lang,
      },
    });
    return WpApiHelper.transformIApiBookletsToIBooklets(response.data);
  }

  public async getKnowledgeBaseOverview(lang: ELanguage): Promise<{ data: IKnowledgeBaseOverview; success: boolean }> {
    const response = await WpApiRoutes.getKnowledgeBaseOverview.fetch({
      data: {
        lang,
      },
    });
    return {
      data: response.data,
      success: response.success as boolean,
    };
  }

  public async getMedia(action: string, filter: number, page: number): Promise<{ count: number; data: IMediaPost[] }> {
    const response = await WpApiRoutes.getMedia.fetch({
      data: {
        action,
        filter,
        page,
      },
    });

    return {
      count: response.count as number,
      data: WpApiHelper.transformIApiMediaPostsToIMediaPosts(response.data),
    };
  }

  public async getNews(isPartner: boolean, lang: ELanguage, limit: number, page: number): Promise<INewsEntry[]> {
    const response = await WpApiRoutes.getNews.fetch({
      data: {
        isPartner,
        lang,
        limit,
        page,
      },
    });
    return response.data;
  }

  public async getShippingInfo(lang: ELanguage): Promise<IShippingCost[]> {
    const response = await WpApiRoutes.getShippingInfo.fetch({
      data: {
        lang,
      },
    });
    return response.data;
  }

  public async getRethinkArticles(action: string, lang: ELanguage): Promise<IArticleCard[]> {
    const response = await WpApiRoutes.getRethinkArticles.fetch({
      data: {
        action,
        wpml_lang: lang,
      },
    });
    return WpApiHelper.transformIApiRethinkArticlesToIArticleCards(response);
  }

  public async getWpPageUrls(pageId: number): Promise<TApiWpPages> {
    return WpApiRoutes.getUrls.fetch({
      data: {
        pageid: pageId,
      },
    });
  }

  public async getWpPostId(url: string): Promise<IPostId> {
    const response = await WpApiRoutes.getPostId.fetch({
      data: {
        url,
      },
    });
    return response.data;
  }

  // eslint-disable-next-line camelcase
  public async getSliderBlogPosts(data: { topic: number; wpml_lang: ELanguage }): Promise<{count: number; data: ISliderBlogPost[]}> {
    const response = await WpApiRoutes.getSliderPosts.fetch({ data });
    return {
      count: response.count as number,
      data: WpApiHelper.transformIApiSliderBlogPostsToISliderBlogPosts(response.data),
    };
  }

  // eslint-disable-next-line camelcase
  public async getPreviewBlogPosts(data: { page?: number; topic: number; wpml_lang: ELanguage }): Promise<{count: number; data: IPreviewBlogPost[]}> {
    const response = await WpApiRoutes.getPreviewPosts.fetch({ data });
    return {
      count: response.count as number,
      data: WpApiHelper.transformIApiPreviewBlogPostsToIPreviewBlogPosts(response.data),
    };
  }

  public async getWpMenuItems(lang: ELanguage): Promise<IMenus> {
    return WpApiRoutes.getMenuItems.fetch({
      data: {
        lang,
      },
    });
  }

  public async getWpMenuItemsForPartners(lang: ELanguage): Promise<IMenus> {
    return WpApiRoutes.getMenuItemsForPartners.fetch({
      data: {
        lang,
      },
    });
  }

  public async getSingleProductData(slug: string, lang: ELanguage, imagesBaseUrl: string): Promise<ISingleProductData> {
    const response = await WpApiRoutes.getSingleProductData.fetch({
      data: {
        slug,
        wpml_lang: lang,
      },
    });
    return WpApiHelper.transformIApiSingleProductDataToISingleProductData(response[0], imagesBaseUrl);
  }

  public async getExtendedSingleProductData(slug: string, lang: ELanguage, imagesBaseUrl: string): Promise<IExtendedSingleProductData> {
    const response = await WpApiRoutes.getExtendedSingleProductData.fetch({
      data: {
        slug,
        wpml_lang: lang,
      },
    });
    return WpApiHelper.transformIApiExtendedSingleProductDataToIExtendedSingleProductData(response[0], imagesBaseUrl);
  }

  public async getSummits(): Promise<ISummitDataWP[]> {
    const response = await WpApiRoutes.getSummits.fetch();
    return response.data;
  }

  public async getSingleSummitData(id: number, lang: ELanguage): Promise<ISingleSummitWPData> {
    const response = await WpApiRoutes.getSingleSummitData.fetch({
      data: {
        id,
        lang,
      },
    });
    return response.data;
  }

  public async getTransparencyReports(lang: ELanguage): Promise<ITransparencyReport[]> {
    const response = await WpApiRoutes.getTransparencyReports.fetch({
      data: {
        lang,
      },
    });
    return response.data ?? [];
  }
}
